import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/api';

// Platform Icons
const YoutubeIcon = () => (
  <img src="/images/categories/youtube.svg" alt="YouTube" className="w-16 h-16 sm:w-20 sm:h-20" />
);

const InstagramIcon = () => (
  <img src="/images/categories/instagram.svg" alt="Instagram" className="w-16 h-16 sm:w-20 sm:h-20" />
);

const TiktokIcon = () => (
  <img src="/images/categories/tiktok.svg" alt="TikTok" className="w-16 h-16 sm:w-20 sm:h-20" />
);

const FacebookIcon = () => (
  <img src="/images/categories/facebook.svg" alt="Facebook" className="w-16 h-16 sm:w-20 sm:h-20" />
);

// Badge icons
const VerifiedIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
);
const MonetizedIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13v2.062A3.001 3.001 0 0113 10c0 1.306-.835 2.417-2 2.83V15a1 1 0 11-2 0v-2.062A3.001 3.001 0 017 10c0-1.306.835-2.417 2-2.83V5a1 1 0 112 0zm-1 4a1 1 0 100 2 1 1 0 000-2z"/></svg>
);
const EmailIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
);

const FollowersIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-1" fill="currentColor" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C16.16 13.36 18 14.28 18 15.5V19h6v-2.5c0-2.33-4.67-3.5-8-3.5z"/></svg>
);

// Helper to slugify product title
function slugify(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

const ProductCard = ({ product, width = 360, height = 560, showEmail = true, onDetails, featured = false }) => {
  const navigate = useNavigate();
  if (!product) return null;
  // Centralized logic for all fields
  const image = product.mainImage ? getImageUrl(product.mainImage) : product.image;
  const title = product.title;
  const category = product.stats?.category || product.category;
  const followers = product.stats?.followers || product.followers;
  const monetized = product.stats?.monetized === 'yes' || product.monetized;
  const verified = product.verified;
  const price = product.salePrice || product.price;
  const oldPrice = product.salePrice ? product.price : undefined;
  const onSale = !!product.salePrice;
  const accountType = product.accountType || product.platform || '';

  function getPlatformIcon(type) {
    switch (type) {
      case 'youtube':
        return <YoutubeIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'tiktok':
        return <TiktokIcon />;
      case 'facebook':
        return <FacebookIcon />;
      default:
        return null;
    }
  }

  return (
    <a
      itemScope
      itemType="https://schema.org/Product"
      href={product.slug ? `/product/${product.slug}` : `/product/${product._id}`}
      className="w-full bg-white dark:bg-[#282828] rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-300 mx-auto hover:shadow-xl hover:scale-[1.02] hover:z-10 hover:border-red-500/40 dark:hover:border-red-500/40 dark:hover:shadow-2xl dark:hover:shadow-red-500/10 cursor-pointer"
      style={{ textDecoration: 'none' }}
    >
      <meta itemProp="name" content={title} />
      <meta itemProp="image" content={image} />
      <meta itemProp="price" content={price} />
      <meta itemProp="priceCurrency" content="USD" />
      <meta itemProp="availability" content={product.stockStatus === 'outofstock' ? 'OutOfStock' : 'InStock'} />
      {/* Blue highlight bar for boosted */}
      {product.boosted && (
        <div className="absolute top-0 left-0 w-full h-1 sm:h-1.5 lg:h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-t-lg sm:rounded-t-xl lg:rounded-t-2xl"></div>
      )}
      {/* Image */}
      <div className="w-full aspect-[4/3] bg-gray-100 overflow-hidden relative">
        {product.boosted && (
          <div className="absolute top-1.5 left-1.5 sm:top-2 lg:top-0 sm:left-2 lg:left-0 z-10">
            <span className="inline-flex items-center justify-center bg-blue-600 dark:bg-blue-500 text-white font-bold px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg lg:rounded-xl shadow-lg dark:shadow-blue-500/30 border border-blue-400 dark:border-blue-300">
              {/* Large icon only on mobile */}
              <svg className="w-6 h-6 sm:hidden" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
              {/* Icon + text on sm and up */}
              <span className="hidden sm:inline-flex items-center">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 mr-1 -ml-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
                <span className="hidden lg:inline">Boosted</span>
                <span className="lg:hidden">Boost</span>
              </span>
            </span>
          </div>
        )}
        {featured && (
          <div className="absolute top-1.5 right-1.5 sm:top-2 lg:top-3 sm:right-2 lg:right-3 z-20">
            <span className="inline-flex items-center px-2 sm:px-2 lg:px-2 py-1 sm:py-1 lg:py-1 bg-red-600 text-white text-xs sm:text-xs lg:text-xs font-bold rounded-full shadow-lg border border-white">
              <svg className="w-3 h-3 sm:w-3 sm:h-3 lg:w-4 lg:h-4 mr-1 sm:mr-1 lg:mr-1" fill="currentColor" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 17.75l-6.172 3.245 1.179-6.873L2 9.505l6.914-1.004L12 2.75l3.086 5.751L22 9.505l-5.007 4.617 1.179 6.873z" /></svg>
              <span className="text-xs font-bold">Featured</span>
            </span>
          </div>
        )}
        <img
          itemProp="image"
          src={image}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      {/* Category Row */}
      <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-5 mt-1.5 sm:mt-2 lg:mt-3 mb-2 sm:mb-3 lg:mb-4">
        {/* Category badge */}
        <div className="flex items-center p-0.5 sm:p-1 lg:p-1 pr-1.5 sm:pr-2 lg:pr-3 rounded-md sm:rounded-lg lg:rounded-[12px] border border-white/60 bg-white/60 backdrop-blur-md shadow-md">
          <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-gradient-to-br from-white to-gray-200 border border-white/60 mr-1 sm:mr-1.5 lg:mr-2">
            <img
              src={`/images/categories/${(accountType || category || '').toLowerCase()}.svg`}
              alt={accountType || category}
              className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4"
              loading="lazy"
            />
          </div>
          <span className="text-xs sm:text-sm lg:text-base font-semibold text-black capitalize drop-shadow-sm">
            {(accountType || category || '').charAt(0).toUpperCase() + (accountType || category || '').slice(1)}
          </span>
        </div>
        {/* Monetized badge */}
        {(monetized || product.stats?.monetized === 'yes') && (
          <div className="flex items-center p-0.5 sm:p-1 lg:p-1 pr-1.5 sm:pr-2 lg:pr-3 rounded-md sm:rounded-lg lg:rounded-[12px] border border-white/60 bg-white/60 backdrop-blur-md shadow-md ml-1 sm:ml-2">
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-gradient-to-br from-white to-gray-200 border border-white/60 mr-1 sm:mr-1.5 lg:mr-2">
              <img
                src={"/images/categories/coin.svg"}
                alt="Monetized"
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4"
                loading="lazy"
              />
            </div>
            <span className="text-xs sm:text-sm lg:text-base font-semibold text-black drop-shadow-sm">
              Monetized
            </span>
          </div>
        )}
      </div>
      {/* Title */}
      <div className="px-2 sm:px-3 lg:px-5">
        <h3
          itemProp="name"
          className="font-bold text-sm sm:text-lg lg:text-xl text-gray-900 dark:text-white leading-tight mb-1.5 sm:mb-2 lg:mb-2 cursor-pointer hover:text-red-600 dark:hover:text-red-400 transition-colors mt-0"
          title={title}
          style={{ textDecoration: 'none', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {title}
        </h3>
        {/* Followers */}
        {typeof followers !== 'undefined' && followers !== '' && (
          <>
            <div className="flex items-center gap-1 text-xs sm:text-sm lg:text-base font-semibold text-gray-900 dark:text-gray-200 mb-1.5 sm:mb-2 lg:mb-3 mt-1">
              <FollowersIcon />
              <span className="tabular-nums">{Number(followers).toLocaleString()}</span>
            </div>
            <div className="border-b border-gray-200 dark:border-gray-600 mb-2 sm:mb-3 lg:mb-4" />
          </>
        )}
      </div>
      {/* Price Row */}
      <div className="flex items-center px-2 sm:px-3 lg:px-5 mb-2 sm:mb-3 lg:mb-4 mt-auto gap-1.5 sm:gap-2">
        <span className="text-sm sm:text-base lg:text-lg font-bold text-black dark:text-white">USD</span>
        <span className="text-sm sm:text-base lg:text-lg font-bold text-black dark:text-white">${price}</span>
        {product.stockStatus === 'outofstock' ? (
          <span className="ml-1.5 sm:ml-2 bg-gray-500 text-white text-[10px] sm:text-sm lg:text-base px-1 sm:px-2 lg:px-3 py-0 sm:py-1 rounded-sm sm:rounded-full font-bold">out of stock</span>
        ) : (
          <>
            {oldPrice && (
              <span className="text-gray-400 dark:text-gray-500 line-through text-xs sm:text-sm lg:text-base ml-1">${oldPrice}</span>
            )}
            {onSale && (
              <span className="ml-1.5 sm:ml-2 bg-red-500 text-white text-xs px-1.5 sm:px-2 lg:px-3 py-0.5 sm:py-1 rounded-full font-bold">Sale!</span>
            )}
          </>
        )}
      </div>
      {/* Details Button */}
      <div className="px-2 sm:px-3 lg:px-5 pb-2 sm:pb-3 lg:pb-5 mt-1.5 sm:mt-2">
        <span
          className={`w-full border font-semibold py-2 sm:py-2.5 lg:py-2 rounded-md sm:rounded-lg lg:rounded-xl transition-all duration-300 text-xs sm:text-sm lg:text-base flex items-center justify-center ${product.stockStatus === 'outofstock' ? 'bg-gray-300 text-gray-500 border-gray-300 hover:bg-gray-400 hover:text-white' : 'border-gray-400 dark:border-gray-600 hover:bg-red-600 hover:text-white text-gray-900 dark:text-white dark:hover:bg-red-500 dark:hover:text-white dark:hover:border-red-500 hover:shadow-lg dark:hover:shadow-red-500/20'}`}
        >
          View Details
        </span>
      </div>
    </a>
  );
};

export default ProductCard;